import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Menu, X, ChevronDown,
  ShoppingCart, Code2, Server, Mail, ShieldCheck, Plug, Megaphone,
} from 'lucide-react';

const MotionLink = motion(Link);

const servicePillars = [
  { name: 'Shopify & eCommerce',         href: '/services/shopify',         icon: ShoppingCart, desc: 'Themes, Plus, apps, migrations' },
  { name: 'Software & Mobile',           href: '/services/software',        icon: Code2,        desc: 'Web, iOS, Android, SaaS' },
  { name: 'IT Infrastructure',           href: '/services/it-solutions',    icon: Server,       desc: 'Networks, telephony, on-prem' },
  { name: 'Email & Workplace',           href: '/services/email-workplace', icon: Mail,         desc: 'Microsoft 365, Google Workspace' },
  { name: 'Cybersecurity',               href: '/services/networking',      icon: ShieldCheck,  desc: 'Network security, endpoints, audits' },
  { name: 'Integrations & Partnerships', href: '/services/integrations',    icon: Plug,         desc: 'Zoho, Microsoft, Interakt, APIs' },
  { name: 'Marketing & Creative',        href: '/services/marketing',       icon: Megaphone,    desc: 'Branding, social, ads, content, SEO' },
];

const navLinks = [
  { name: 'Why Us',     href: '#why-us' },
  { name: 'Industries', href: '#industries' },
  { name: 'About',      href: '#about' },
  { name: 'Contact',    href: '#contact' },
];

export const Navbar = () => {
  const [isScrolled, setIsScrolled]             = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen]     = useState(false);
  const dropdownRef                             = useRef(null);
  const navigate                                = useNavigate();
  const location                                = useLocation();

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Cross-page-aware anchor navigation:
  // if already on home, just smooth-scroll; otherwise route to home then scroll.
  const goToAnchor = (href) => {
    setIsMobileMenuOpen(false);
    if (location.pathname === '/') {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/' + href); // /#section
      // After route change, scroll to anchor
      setTimeout(() => {
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  const onServicesClick = () => {
    if (location.pathname === '/') {
      const el = document.querySelector('#services');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/#services');
      setTimeout(() => {
        const el = document.querySelector('#services');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
    setIsServicesOpen(false);
  };

  return (
    <>
      <motion.nav
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${
          isScrolled
            ? 'py-3 border-b border-[#E2E8F0]'
            : 'bg-transparent py-5'
        }`}
        style={isScrolled ? { background: 'rgba(255, 255, 255, 0.92)', backdropFilter: 'blur(20px)' } : {}}
        data-testid="navbar"
      >
        <div className="container-custom flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo">
            <span className="font-display text-[1.4rem] font-bold text-[#0F172A] tracking-tight">Smart</span>
            <span className="font-display text-[1.4rem] font-bold text-[#2563EB] tracking-tight">Solutions</span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden xl:flex items-center gap-6">
            {/* Services dropdown */}
            <div ref={dropdownRef} className="relative">
              <motion.button
                onClick={onServicesClick}
                onMouseEnter={() => setIsServicesOpen(true)}
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 }}
                className="relative flex items-center gap-1 text-[#374151] hover:text-[#0F172A] text-sm font-medium transition-colors duration-300 group"
                data-testid="nav-services"
              >
                Services
                <ChevronDown size={13} className={`transition-transform duration-300 ${isServicesOpen ? 'rotate-180' : ''}`} />
                <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#2563EB] transition-all duration-300 group-hover:w-full" />
              </motion.button>

              <AnimatePresence>
                {isServicesOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.18 }}
                    onMouseLeave={() => setIsServicesOpen(false)}
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[480px] bg-white border border-[#E3E8EE] rounded-xl shadow-[0_20px_60px_rgba(15,23,42,0.10)] overflow-hidden"
                  >
                    <div className="px-5 pt-4 pb-2 border-b border-[#E3E8EE] flex items-center justify-between">
                      <span className="font-mono text-[0.55rem] tracking-[0.22em] uppercase text-[#8898AA] font-medium">
                        Seven Practice Areas
                      </span>
                      <button
                        onClick={onServicesClick}
                        className="text-[0.7rem] font-semibold text-[#2563EB] hover:text-[#1D4ED8] transition-colors"
                      >
                        View all →
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-px bg-[#F1F5F9]">
                      {servicePillars.map((p) => {
                        const Icon = p.icon;
                        return (
                          <Link
                            key={p.href}
                            to={p.href}
                            onClick={() => setIsServicesOpen(false)}
                            className="group bg-white px-4 py-3.5 hover:bg-[#F8FAFC] transition-colors duration-200 flex items-start gap-3"
                          >
                            <div className="w-8 h-8 rounded-md bg-[#EFF6FF] flex items-center justify-center shrink-0 group-hover:bg-[#2563EB] transition-colors duration-300">
                              <Icon size={15} className="text-[#2563EB] group-hover:text-white transition-colors duration-300" />
                            </div>
                            <div className="min-w-0">
                              <div className="text-[0.78rem] font-semibold text-[#0F172A] leading-tight mb-0.5 truncate">
                                {p.name}
                              </div>
                              <div className="text-[0.65rem] text-[#64748B] leading-snug truncate">
                                {p.desc}
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Other nav items */}
            {navLinks.map((link, i) => (
              <motion.button
                key={link.name}
                onClick={() => goToAnchor(link.href)}
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.13 + i * 0.05 }}
                className="relative text-[#374151] hover:text-[#0F172A] text-sm font-medium transition-colors duration-300 group"
                data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
              >
                {link.name}
                <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#2563EB] transition-all duration-300 group-hover:w-full" />
              </motion.button>
            ))}
          </div>

          {/* CTA */}
          <div className="hidden xl:block">
            <MotionLink
              to="/book"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="text-sm font-medium px-5 py-2.5 rounded-full border border-[#2563EB]/60 text-[#2563EB] hover:bg-[#2563EB] hover:text-white transition-all duration-300 inline-block"
              data-testid="nav-cta-button"
            >
              Book a Call
            </MotionLink>
          </div>

          {/* Mobile toggle */}
          <button
            className="xl:hidden p-2 text-[#0F172A]"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </motion.nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 xl:hidden"
            data-testid="mobile-menu"
          >
            <div
              className="absolute inset-0 bg-black/30 backdrop-blur-sm"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 200 }}
              className="absolute right-0 top-0 h-full w-[320px] bg-white border-l border-[#E2E8F0] p-8 pt-24 overflow-y-auto"
            >
              <div className="flex flex-col gap-1">
                {/* Mobile Services collapsible */}
                <button
                  onClick={() => setIsMobileServicesOpen((v) => !v)}
                  className="flex items-center justify-between w-full text-left text-base font-medium text-[#374151] hover:text-[#0F172A] py-4 border-b border-[#E2E8F0] transition-colors"
                  data-testid="mobile-nav-services"
                >
                  <span>Services</span>
                  <ChevronDown size={15} className={`transition-transform duration-300 ${isMobileServicesOpen ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {isMobileServicesOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="pl-3 py-2 flex flex-col">
                        {servicePillars.map((p) => {
                          const Icon = p.icon;
                          return (
                            <Link
                              key={p.href}
                              to={p.href}
                              onClick={() => setIsMobileMenuOpen(false)}
                              className="flex items-center gap-3 py-2.5 text-[0.85rem] text-[#374151] hover:text-[#2563EB] transition-colors"
                            >
                              <Icon size={14} className="text-[#2563EB] shrink-0" />
                              {p.name}
                            </Link>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {navLinks.map((link, i) => (
                  <motion.button
                    key={link.name}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.06 }}
                    onClick={() => goToAnchor(link.href)}
                    className="text-left text-base font-medium text-[#374151] hover:text-[#0F172A] py-4 border-b border-[#E2E8F0] transition-colors"
                    data-testid={`mobile-nav-${link.name.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.name}
                  </motion.button>
                ))}
                <Link
                  to="/book"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="mt-6 w-full py-3 rounded-full bg-[#2563EB] text-white text-sm font-semibold hover:bg-[#1D4ED8] transition-colors duration-300 block text-center"
                  data-testid="mobile-cta-button"
                >
                  Book a Call
                </Link>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
